import { useEffect, useRef } from 'react';
import {
  useActiveWallet,
  useActiveAccount,
  useAutoConnect,
  useDisconnect,
} from 'thirdweb/react';
import { useQueryClient } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { toast } from 'sonner';
import { useAuthStore } from '../../hooks';
import { useGetUserById } from '../../hooks/use-user';
import { client } from '../../lib/client';
import { clearSelectedCommunity } from '../../utils/community-cookie';

export default function WalletListener() {
  const wallet = useActiveWallet();
  const activeAccount = useActiveAccount();
  const { disconnect } = useDisconnect();
  const updateWalletAddress = useAuthStore(
    (state) => state.updateWalletAddress
  );
  const storeUser = useAuthStore((state) => state.user);
  const queryClient = useQueryClient();
  const previousAddressRef = useRef(null);
  const hasInitializedRef = useRef(false);
  const hasCheckedUserRef = useRef(false);

  // Auto-connect on mount
  const { isLoading: isAutoConnecting } = useAutoConnect({ client });

  // Check if wallet user exists in database (only if not already logged in via Passport)
  const shouldCheckUser = activeAccount?.address && !storeUser?.passportId && !isAutoConnecting;
  const { data: walletUser, isFetched: userQueryFetched } = useGetUserById(
    shouldCheckUser ? activeAccount.address : null
  );

  // Sync wallet address to auth store
  useEffect(() => {
    updateWalletAddress(activeAccount?.address || null);
  }, [activeAccount?.address, updateWalletAddress]);

  // Disconnect wallet if user doesn't exist in database
  // This allows user to try a different wallet or use Passport SSO
  useEffect(() => {
    // Wait for auto-connect to finish
    if (isAutoConnecting) return;

    // Only check once per wallet connection
    if (hasCheckedUserRef.current) return;

    // Need an active wallet and completed user query
    if (!activeAccount?.address || !userQueryFetched) return;

    // If we're checking and user doesn't exist (null from 404)
    if (walletUser === null && shouldCheckUser) {
      hasCheckedUserRef.current = true;

      console.log('[WalletListener] No user found for wallet, disconnecting...');

      // Clear cookies
      Cookies.remove('omni_acc', { path: '/' });
      Cookies.remove('jwt', { path: '/' });

      // Disconnect the wallet
      if (wallet) {
        disconnect(wallet);
      }

      toast.info('Wallet Not Registered', {
        description: 'This wallet is not linked to an account. Please sign in with Passport or try a different wallet.',
        duration: 6000,
      });
    } else if (walletUser) {
      // User exists, mark as checked
      hasCheckedUserRef.current = true;
    }
  }, [activeAccount?.address, walletUser, userQueryFetched, isAutoConnecting, shouldCheckUser, wallet, disconnect]);

  // Reset the check ref when wallet changes
  useEffect(() => {
    if (!activeAccount?.address) {
      hasCheckedUserRef.current = false;
    }
  }, [activeAccount?.address]);

  // Handle wallet changes - clear cookies and invalidate queries
  useEffect(() => {
    // Don't do anything while auto-connect is still loading
    if (isAutoConnecting) return;

    const currentAddress = activeAccount?.address?.toLowerCase() || null;
    const previousAddress = previousAddressRef.current;

    // Mark as initialized after first stable state
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      previousAddressRef.current = currentAddress;
      return;
    }

    // If wallet changed (not initial load)
    if (previousAddress !== null && currentAddress !== previousAddress) {
      // Clear the selected community cookie
      clearSelectedCommunity();

      // IMPORTANT: Clear the JWT cookie when wallet changes
      // This forces re-authentication with the new wallet
      Cookies.remove('jwt', { path: '/' });
      console.log('[WalletListener] Wallet changed, cleared JWT cookie');

      // Invalidate all user-related queries to force fresh data
      queryClient.invalidateQueries(['user']);
      queryClient.invalidateQueries(['auth']);
      queryClient.invalidateQueries(['community-admin']);
      queryClient.invalidateQueries(['notifications']);

      // Clear specific cached user data
      queryClient.removeQueries(['user', previousAddress]);

      if (currentAddress) {
        // Wallet switched to a different account
        toast.info('Wallet Changed', {
          description:
            'You switched to a different wallet. Please complete registration if this is a new account.',
          duration: 5000,
        });
      } else {
        // Wallet disconnected
        toast.info('Wallet Disconnected', {
          description: 'Your wallet has been disconnected.',
          duration: 3000,
        });
      }
    }

    // Update the previous address ref
    previousAddressRef.current = currentAddress;
  }, [activeAccount?.address, queryClient, isAutoConnecting]);

  // Set or remove omni_acc cookie when activeAccount changes
  useEffect(() => {
    // Don't do anything while auto-connect is still loading
    if (isAutoConnecting) return;

    if (activeAccount?.address) {
      // Get the current cookie value
      const currentCookie = Cookies.get('omni_acc');

      // If cookie exists and is different from the current wallet, clear JWT first
      if (
        currentCookie &&
        currentCookie.toLowerCase() !== activeAccount.address.toLowerCase()
      ) {
        console.log(
          `[WalletListener] Wallet mismatch detected! Cookie: ${currentCookie.slice(
            0,
            10
          )}... → New: ${activeAccount.address.slice(0, 10)}...`
        );
        // Clear the old JWT since it belongs to the old wallet
        Cookies.remove('jwt', { path: '/' });
        console.log(
          '[WalletListener] Cleared stale JWT cookie due to wallet switch'
        );
      }

      // Now set the new wallet address
      // Must use SameSite=None and Secure for cross-origin requests (different ports on localhost)
      Cookies.set('omni_acc', activeAccount.address, {
        expires: 7,
        path: '/',
        secure: true,
        sameSite: 'none',
      });
      console.log(
        `[WalletListener] Set omni_acc cookie to: ${activeAccount.address.slice(
          0,
          10
        )}...`
      );
    }
    // NOTE: We intentionally don't clear cookies when address is null/undefined
    // because this can happen during loading states. Cookies will be cleared
    // explicitly when user disconnects wallet (detected in the previous useEffect)
  }, [activeAccount?.address, isAutoConnecting]);

  return null;
}
